import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fmtCurrency, fmtDate, formatApiError, mutateOrQueue } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Printer, DollarSign, Wrench, Undo2, Receipt } from "lucide-react";

function PayModal({ invoice, onClose, onSaved }) {
  const due = invoice.total - (invoice.amount_paid || 0);
  const [amount, setAmount] = useState(due);
  const [method, setMethod] = useState("cash");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const res = await mutateOrQueue({
        method: "post",
        url: `/invoices/${invoice.id}/pay`,
        body: { amount: Number(amount), method },
        label: `Payment · ${invoice.number} · ${method} · ₹${amount}`,
      });
      if (res.queued) toast.success("Offline — payment queued and will sync when you reconnect");
      else toast.success("Payment recorded");
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-zinc-950/40 z-50 flex items-center justify-center p-4 no-print" onClick={onClose}>
      <div className="bg-white border border-zinc-300 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="pay-modal">
        <div className="px-6 py-4 border-b border-zinc-200">
          <div className="label-eyebrow">Record payment</div>
          <h3 className="text-xl font-semibold text-zinc-950">{invoice.number}</h3>
          <p className="text-xs font-mono text-zinc-500 mt-1">Due: {fmtCurrency(due)}</p>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="label-eyebrow block mb-2">Amount</label>
            <input data-testid="pay-amount-input" type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="w-full border border-zinc-300 px-3 py-2 font-mono focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
          </div>
          <div>
            <label className="label-eyebrow block mb-2">Method</label>
            <select data-testid="pay-method-input" value={method} onChange={(e) => setMethod(e.target.value)} className="w-full border border-zinc-300 px-3 py-2 font-mono focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
              <option value="cash">Cash</option>
              <option value="card">Card</option>
              <option value="upi">UPI</option>
              <option value="bank_transfer">Bank Transfer</option>
              <option value="online">Online (gateway coming soon)</option>
            </select>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-zinc-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 border border-zinc-300 text-sm uppercase tracking-wider hover:bg-zinc-50">Cancel</button>
          <button data-testid="pay-confirm-button" disabled={busy} onClick={save} className="px-4 py-2 bg-[#002FA7] text-white text-sm uppercase tracking-wider hover:bg-blue-900 disabled:opacity-50">{busy ? "Saving..." : "Confirm"}</button>
        </div>
      </div>
    </div>
  );
}

function ReturnModal({ invoice, lineIndex, onClose, onSaved }) {
  const line = invoice.items[lineIndex];
  const [selected, setSelected] = useState([]);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const unitIds = line.unit_ids || [];
  const serials = line.serials || [];

  const toggle = (uid) => setSelected(selected.includes(uid) ? selected.filter((x) => x !== uid) : [...selected, uid]);

  const submit = async () => {
    if (selected.length === 0) return toast.error("Select at least one unit");
    setBusy(true);
    try {
      await api.post("/returns", { invoice_id: invoice.id, line_index: lineIndex, unit_ids: selected, reason });
      toast.success("Return request raised");
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-zinc-950/40 z-50 flex items-center justify-center p-4 no-print" onClick={onClose}>
      <div className="bg-white border border-zinc-300 w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="return-modal">
        <div className="px-6 py-4 border-b border-zinc-200">
          <div className="label-eyebrow">Raise return</div>
          <h3 className="text-lg font-semibold text-zinc-950">{line.part_number} · {line.manufacturer}</h3>
          <p className="text-xs text-zinc-500 mt-1">Select the specific physical units to return</p>
        </div>
        <div className="p-6 space-y-4">
          <div className="max-h-64 overflow-y-auto border border-zinc-200">
            {unitIds.map((uid, i) => (
              <label key={uid} className="flex items-center gap-3 px-3 py-2 border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer">
                <input type="checkbox" checked={selected.includes(uid)} onChange={() => toggle(uid)} data-testid={`return-unit-${i}`} />
                <span className="font-mono text-sm">{serials[i] || uid.slice(0, 8)}</span>
              </label>
            ))}
            {unitIds.length === 0 && <div className="p-4 text-sm text-zinc-500 text-center">No serial-tracked units on this line.</div>}
          </div>
          <div>
            <label className="label-eyebrow block mb-2">Reason</label>
            <textarea data-testid="return-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} className="w-full border border-zinc-300 px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" rows={3} placeholder="Damaged on arrival, wrong part shipped, etc." />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-zinc-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 border border-zinc-300 text-sm uppercase tracking-wider hover:bg-zinc-50">Cancel</button>
          <button data-testid="return-submit-button" disabled={busy || selected.length === 0} onClick={submit} className="px-4 py-2 bg-[#002FA7] text-white text-sm uppercase tracking-wider hover:bg-blue-900 disabled:opacity-50">{busy ? "Submitting..." : `Return ${selected.length} unit(s)`}</button>
        </div>
      </div>
    </div>
  );
}

export default function InvoiceDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [inv, setInv] = useState(null);
  const [business, setBusiness] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [retLine, setRetLine] = useState(null);
  const showCost = user.role !== "retail_counter";

  const load = useCallback(
    () => api.get(`/invoices/${id}`).then(({ data }) => setInv(data)).catch(() => nav("/invoices")),
    [id, nav],
  );
  useEffect(() => {
    load();
    api.get("/settings/business").then(({ data }) => setBusiness(data));
  }, [load]);

  if (!inv) return <div className="text-sm font-mono text-zinc-500">Loading...</div>;

  const canPay = ["admin", "accountant", "manager"].includes(user.role) || (user.role === "retail_counter" && inv.customer_id === user.id);
  const canReturn = ["admin", "manager", "retail_counter", "accountant"].includes(user.role);
  const due = inv.total - (inv.amount_paid || 0);

  // GST split: intra-state (buyer state code matches ours) = CGST + SGST; else IGST
  const ourState = business?.state_code || "";
  const buyerState = (inv.customer_gstin || "").slice(0, 2);
  const isIntraState = ourState && buyerState && ourState === buyerState;
  const cgst = isIntraState ? inv.tax / 2 : 0;
  const sgst = isIntraState ? inv.tax / 2 : 0;
  const igst = !isIntraState ? inv.tax : 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between no-print">
        <button onClick={() => nav("/invoices")} className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-zinc-500 hover:text-zinc-950">
          <ArrowLeft className="w-3 h-3" /> Back to invoices
        </button>
        <div className="flex gap-2">
          {canPay && inv.status !== "paid" && (
            <button data-testid="record-payment-button" onClick={() => setPayOpen(true)} className="text-xs uppercase tracking-wider px-3 py-2 border border-zinc-300 bg-white hover:bg-zinc-50 inline-flex items-center gap-1">
              <DollarSign className="w-3.5 h-3.5" /> Record payment
            </button>
          )}
          <button data-testid="print-invoice-button" onClick={() => window.print()} className="text-xs uppercase tracking-wider px-3 py-2 bg-zinc-950 text-white hover:bg-zinc-800 inline-flex items-center gap-1">
            <Printer className="w-3.5 h-3.5" /> Download / Print PDF
          </button>
          <button
            data-testid="print-thermal-button"
            onClick={() => {
              document.body.classList.add("thermal-print");
              const cleanup = () => {
                document.body.classList.remove("thermal-print");
                window.removeEventListener("afterprint", cleanup);
              };
              window.addEventListener("afterprint", cleanup);
              // Give the browser a tick to apply the class before opening the dialog
              setTimeout(() => window.print(), 50);
            }}
            className="text-xs uppercase tracking-wider px-3 py-2 border border-zinc-300 bg-white hover:bg-zinc-50 inline-flex items-center gap-1"
          >
            <Receipt className="w-3.5 h-3.5" /> Thermal (80mm)
          </button>
        </div>
      </div>

      <div className="print-area bg-white border border-zinc-200 p-10 max-w-5xl mx-auto" data-testid="invoice-document">
        <div className="flex justify-between items-start border-b-2 border-zinc-950 pb-6 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-zinc-950 flex items-center justify-center"><Wrench className="w-5 h-5 text-white" /></div>
              <div>
                <div className="label-eyebrow">Tax invoice</div>
                <div className="font-bold text-xl text-zinc-950 tracking-tight">PARTSGRID</div>
              </div>
            </div>
            <p className="text-xs font-mono text-zinc-500 leading-relaxed">
              {business?.name || "PartsGrid"}<br />
              {business?.address}<br />
              GSTIN: {business?.gstin || "—"} · Ph: {business?.phone || "—"}
            </p>
          </div>
          <div className="text-right">
            <div className="label-eyebrow">Invoice number</div>
            <div className="font-mono text-2xl font-bold mt-1 text-zinc-950">{inv.number}</div>
            <div className="font-mono text-xs text-zinc-500 mt-2">Issued: {fmtDate(inv.created_at)}</div>
            <div className={`mt-3 inline-block text-xs uppercase tracking-wider px-2 py-1 border ${
              inv.status === "paid" ? "border-emerald-300 bg-emerald-50 text-emerald-700"
              : inv.status === "partial" ? "border-amber-300 bg-amber-50 text-amber-800"
              : "border-rose-300 bg-rose-50 text-rose-700"
            }`}>{inv.status}</div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-8 mb-8">
          <div>
            <div className="label-eyebrow mb-2">Billed to</div>
            <div className="text-sm font-medium text-zinc-950">{inv.customer_name}</div>
            {inv.customer_gstin && <div className="text-xs font-mono text-zinc-600">GSTIN: {inv.customer_gstin}</div>}
            {inv.customer_phone && <div className="text-xs text-zinc-600">{inv.customer_phone}</div>}
            {inv.customer_address && <div className="text-xs text-zinc-600 whitespace-pre-line">{inv.customer_address}</div>}
          </div>
          <div>
            <div className="label-eyebrow mb-2">Issued by</div>
            <div className="text-sm font-medium text-zinc-950">{inv.created_by_name}</div>
          </div>
        </div>

        <table className="w-full text-xs mb-6 border-t border-b border-zinc-300">
          <thead>
            <tr className="bg-zinc-50 text-zinc-500 uppercase tracking-wider text-[10px] border-b border-zinc-300">
              <th className="text-left px-2 py-3 font-semibold">#</th>
              <th className="text-left px-2 py-3 font-semibold">Part / Description</th>
              <th className="text-left px-2 py-3 font-semibold">Serial(s)</th>
              <th className="text-right px-2 py-3 font-semibold">Qty</th>
              <th className="text-right px-2 py-3 font-semibold">MRP</th>
              <th className="text-right px-2 py-3 font-semibold">Disc</th>
              <th className="text-right px-2 py-3 font-semibold">GST%</th>
              <th className="text-right px-2 py-3 font-semibold">GST ₹</th>
              {showCost && <th className="text-right px-2 py-3 font-semibold">Margin</th>}
              <th className="text-right px-2 py-3 font-semibold">Total</th>
              {canReturn && inv.status !== "unpaid" || canReturn ? <th className="text-right px-2 py-3 font-semibold no-print"></th> : null}
            </tr>
          </thead>
          <tbody>
            {inv.items.map((it, idx) => {
              const margin = ((it.mrp ?? it.unit_price ?? 0) - (it.purchase_price ?? it.cost_price ?? 0)) * it.qty - (it.discount || 0);
              const lineKey = `${it.part_id || it.part_number}-${idx}`;
              return (
                <tr key={lineKey} className="border-b border-zinc-100 align-top">
                  <td className="px-2 py-3 font-mono">{idx + 1}</td>
                  <td className="px-2 py-3">
                    <div className="font-mono font-medium text-zinc-950">{it.part_number}</div>
                    <div className="text-zinc-500">{it.manufacturer} {it.model}{it.variant && ` · ${it.variant}`} {it.name && `— ${it.name}`}</div>
                  </td>
                  <td className="px-2 py-3 font-mono text-[10px] text-zinc-600">
                    {(it.serials || []).map((s, i) => <div key={`${lineKey}-s${i}-${s}`}>{s}</div>)}
                  </td>
                  <td className="px-2 py-3 text-right font-mono">{it.qty}</td>
                  <td className="px-2 py-3 text-right font-mono">{fmtCurrency(it.mrp ?? it.unit_price ?? 0)}</td>
                  <td className="px-2 py-3 text-right font-mono">{fmtCurrency(it.discount || 0)}</td>
                  <td className="px-2 py-3 text-right font-mono">{it.gst_rate ?? 0}%</td>
                  <td className="px-2 py-3 text-right font-mono">{fmtCurrency(it.gst_amount || 0)}</td>
                  {showCost && <td className="px-2 py-3 text-right font-mono text-emerald-700">{fmtCurrency(margin)}</td>}
                  <td className="px-2 py-3 text-right font-mono font-semibold">{fmtCurrency(it.line_total)}</td>
                  {canReturn && (
                    <td className="px-2 py-3 text-right no-print">
                      <button data-testid={`return-line-${idx}`} onClick={() => setRetLine(idx)} className="text-[10px] uppercase tracking-wider px-2 py-1 border border-zinc-300 hover:bg-zinc-50 inline-flex items-center gap-1">
                        <Undo2 className="w-3 h-3" /> Return
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>

        <div className="flex justify-end">
          <div className="w-80 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-zinc-500">Subtotal</span><span className="font-mono">{fmtCurrency(inv.subtotal)}</span></div>
            {isIntraState ? (
              <>
                <div className="flex justify-between text-xs"><span className="text-zinc-500">CGST</span><span className="font-mono">{fmtCurrency(cgst)}</span></div>
                <div className="flex justify-between text-xs"><span className="text-zinc-500">SGST</span><span className="font-mono">{fmtCurrency(sgst)}</span></div>
              </>
            ) : (
              <div className="flex justify-between text-xs"><span className="text-zinc-500">IGST</span><span className="font-mono">{fmtCurrency(igst)}</span></div>
            )}
            {(inv.returned_amount || 0) > 0 && <div className="flex justify-between text-rose-600"><span>Returned</span><span className="font-mono">- {fmtCurrency(inv.returned_amount)}</span></div>}
            <div className="flex justify-between border-t border-zinc-300 pt-2 font-bold"><span>Total</span><span className="font-mono text-lg">{fmtCurrency(inv.total)}</span></div>
            <div className="flex justify-between text-emerald-700"><span className="text-zinc-500">Paid</span><span className="font-mono">{fmtCurrency(inv.amount_paid)}</span></div>
            <div className="flex justify-between font-bold border-t border-zinc-300 pt-2"><span>Balance due</span><span className="font-mono text-lg">{fmtCurrency(due)}</span></div>
          </div>
        </div>

        <div className="border-t border-zinc-200 mt-12 pt-4 font-mono text-[11px] text-zinc-400 uppercase tracking-wider text-center">
          Generated by PartsGrid · Thank you for your business
        </div>
      </div>

      {/* Thermal 80mm receipt — hidden on screen; only visible when body.thermal-print is set for printing */}
      <div className="thermal-receipt" data-testid="thermal-receipt">
        <div className="th-title">{business?.name || "PartsGrid"}</div>
        {business?.address && <div className="th-sub">{business.address}</div>}
        <div className="th-sub">
          {business?.gstin ? `GSTIN: ${business.gstin}` : ""}
          {business?.gstin && business?.phone ? " · " : ""}
          {business?.phone ? `Ph: ${business.phone}` : ""}
        </div>
        <hr className="th-hr" />
        <div className="th-row"><span>Invoice</span><span>{inv.number}</span></div>
        <div className="th-row"><span>Date</span><span>{fmtDate(inv.created_at)}</span></div>
        <div className="th-row"><span>Cashier</span><span>{inv.created_by_name}</span></div>
        <div className="th-row"><span>Customer</span><span>{inv.customer_name || "Walk-in"}</span></div>
        {inv.customer_phone && <div className="th-row"><span>Phone</span><span>{inv.customer_phone}</span></div>}
        {inv.customer_gstin && <div className="th-row"><span>GSTIN</span><span>{inv.customer_gstin}</span></div>}
        <hr className="th-hr" />
        <table>
          <thead>
            <tr>
              <th style={{ textAlign: "left" }}>Item</th>
              <th style={{ textAlign: "right" }}>Qty</th>
              <th style={{ textAlign: "right" }}>Rate</th>
              <th style={{ textAlign: "right" }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {inv.items.map((it, idx) => (
              <tr key={`th-${idx}-${it.part_number}`}>
                <td>
                  <div className="th-item-desc">{it.part_number}</div>
                  <div className="th-item-meta">{it.manufacturer} {it.model}{it.name ? ` · ${it.name}` : ""}</div>
                </td>
                <td style={{ textAlign: "right", verticalAlign: "top" }}>{it.qty}</td>
                <td style={{ textAlign: "right", verticalAlign: "top" }}>{Number(it.mrp ?? it.unit_price ?? 0).toFixed(2)}</td>
                <td style={{ textAlign: "right", verticalAlign: "top" }}>{Number(it.line_total).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <hr className="th-hr" />
        <div className="th-row"><span>Subtotal</span><span>{fmtCurrency(inv.subtotal)}</span></div>
        {isIntraState ? (
          <>
            <div className="th-row"><span>CGST</span><span>{fmtCurrency(cgst)}</span></div>
            <div className="th-row"><span>SGST</span><span>{fmtCurrency(sgst)}</span></div>
          </>
        ) : (
          <div className="th-row"><span>IGST</span><span>{fmtCurrency(igst)}</span></div>
        )}
        {(inv.returned_amount || 0) > 0 && (
          <div className="th-row"><span>Returned</span><span>- {fmtCurrency(inv.returned_amount)}</span></div>
        )}
        <hr className="th-hr" />
        <div className="th-row th-total"><span>TOTAL</span><span>{fmtCurrency(inv.total)}</span></div>
        <div className="th-row"><span>Paid</span><span>{fmtCurrency(inv.amount_paid)}</span></div>
        <div className="th-row th-total"><span>DUE</span><span>{fmtCurrency(due)}</span></div>
        <hr className="th-hr" />
        <div className="th-foot">Thank you for your business!</div>
        <div className="th-foot">Powered by PartsGrid</div>
      </div>

      {payOpen && <PayModal invoice={inv} onClose={() => setPayOpen(false)} onSaved={() => { setPayOpen(false); load(); }} />}
      {retLine !== null && <ReturnModal invoice={inv} lineIndex={retLine} onClose={() => setRetLine(null)} onSaved={() => { setRetLine(null); load(); }} />}
    </div>
  );
}
